import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { AlertCircle, Info, Loader2, RefreshCw } from 'lucide-react';
import { reprocessIntakeEndpointIntakeIntakeIdReprocessPost } from '../services/api/sdk.gen';
import type { IntakeRecord } from '../store/slices/intakeSlice';

interface RerunIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intake: IntakeRecord | null;
  onSuccess: () => void;
  getPatientName: (intake: IntakeRecord) => string;
}

const RerunIntakeDialog: React.FC<RerunIntakeDialogProps> = ({
  open,
  onOpenChange,
  intake,
  onSuccess,
  getPatientName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirmRerun = async () => {
    if (!intake) return;
    setLoading(true);
    setError(null);

    try {
      await reprocessIntakeEndpointIntakeIntakeIdReprocessPost({
        path: { intake_id: intake.id },
      });
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('Failed to reprocess intake:', err);
      setError('Failed to reprocess intake. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rerun Intake Processing</DialogTitle>
        </DialogHeader>
        <div className='py-4'>
          {intake && !error && (
            <div className='space-y-4'>
              <p className='text-gray-600'>
                Are you sure you want to reprocess this intake
                {getPatientName(intake) !== 'N/A' && (
                  <>
                    {' '}
                    for <strong>{getPatientName(intake)}</strong>
                  </>
                )}
                ?
              </p>
              {intake.processing_errors && (
                <div className='bg-red-50 p-3 rounded-md border border-red-100 text-sm text-red-800'>
                  <strong>Previous error:</strong> {intake.processing_errors}
                </div>
              )}
              <div className='bg-blue-50 p-3 rounded-md border border-blue-100 text-sm text-blue-800 flex gap-2'>
                <Info className='w-4 h-4 mt-0.5 flex-shrink-0' />
                <span>
                  This will start a new processing workflow for this intake.
                </span>
              </div>
            </div>
          )}
          {error && (
            <Alert className='border-red-200 bg-red-50'>
              <AlertCircle className='h-4 w-4 text-red-600' />
              <AlertDescription className='text-red-800'>
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRerun} disabled={loading}>
            {loading ? (
              <Loader2 className='w-4 h-4 mr-2 animate-spin' />
            ) : (
              <RefreshCw className='w-4 h-4 mr-2' />
            )}
            Rerun Processing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RerunIntakeDialog;
